// Which provider a `/search` turn is armed for.
//
// The slash menu is locked to three items (see slashMenuModel), so TinyFish
// surfaces as a choice *under* Search rather than a fourth entry. Both
// providers produce the same spliced block; only the grounding differs.
import { neutralizeSearchMarkers, type SearchSource } from "./chatModel";

export type SearchProviderId = "exa" | "tinyfish";

export interface SearchProviderOption {
  readonly id: SearchProviderId;
  readonly label: string;
  /** Shown under the label — the tradeoff, in the user's terms. */
  readonly description: string;
}

export const SEARCH_PROVIDERS: readonly SearchProviderOption[] = [
  {
    id: "exa",
    label: "Exa",
    description: "Fast — ranked results with excerpts",
  },
  {
    id: "tinyfish",
    label: "TinyFish",
    description: "Deeper — reads the top pages, slower",
  },
] as const;

/**
 * Narrow an opaque provider string (as read back from a persisted block)
 * to a provider we can actually route to. Guards against a block written by
 * a future or removed provider.
 */
export function isSearchProviderId(v: string | null): v is SearchProviderId {
  return v !== null && SEARCH_PROVIDERS.some((p) => p.id === v);
}

/** BE route for a provider. */
export function endpointFor(provider: SearchProviderId): string {
  return provider === "exa" ? "/api/v1/chat/search" : "/api/v1/chat/web-agent";
}

/**
 * Provider a retry re-arms with.
 *
 * The spliced block records citations but not which provider produced them,
 * so a rehydrated turn cannot be attributed. Exa is the cheaper, faster
 * default; the chip stays visible so the user can switch before sending.
 */
export const RETRY_DEFAULT_PROVIDER: SearchProviderId = "exa";

/** Short label for the armed chip. */
export function labelFor(provider: SearchProviderId): string {
  return SEARCH_PROVIDERS.find((p) => p.id === provider)?.label ?? "Search";
}

/** Host shown on the tool-call card, and the egress the user consented to. */
export function serverFor(provider: SearchProviderId): string {
  return provider === "exa" ? "exa.ai" : "tinyfish.ai";
}

/** A result normalized across providers. */
export interface NormalizedResult {
  title: string;
  url: string;
  /** Grounding text: Exa highlights/excerpt, or a TinyFish page/snippet. */
  text: string;
  /** TinyFish only: true when `text` is fetched page markdown, not the snippet. */
  fetched?: boolean;
}

/** Per-provider raw result shapes, as the BE proxies them. */
interface RawResult {
  title?: unknown;
  url?: unknown;
  text?: unknown;
  highlights?: unknown;
  fetched?: unknown;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");

// Exa returns `highlights` (preferred) or a long `text`; the BE caps neither,
// so the excerpt cap lives here.
const EXA_EXCERPT_CHARS = 1500;

// TinyFish text is bounded server-side too, but this side keeps its own
// cap rather than trusting a constant it cannot see. The previous comment
// asserted "4000 chars" and went stale the moment the BE raised it — a
// coupling that is invisible until a page arrives three times larger than
// the FE expected. Set generously above the server's per-page cap so it is
// a backstop, not a second truncation.
const TINYFISH_MAX_CHARS = 16000;

/**
 * Flatten a provider response into `{title, url, text}`.
 *
 * Defensive by construction: a provider that returns a non-array, or entries
 * missing a url, yields fewer results rather than `undefined` leaking into the
 * spliced block (which previously surfaced as the literal string "undefined"
 * in the model's context).
 */
export function normalizeResults(
  provider: SearchProviderId,
  raw: unknown,
): NormalizedResult[] {
  if (!Array.isArray(raw)) return [];
  const out: NormalizedResult[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const r = entry as RawResult;
    const url = str(r.url).trim();
    if (url === "") continue;
    let text: string;
    if (provider === "exa") {
      const highlights = Array.isArray(r.highlights)
        ? r.highlights.filter((h): h is string => typeof h === "string")
        : [];
      text =
        highlights.length > 0
          ? highlights.join("\n...\n")
          : str(r.text).slice(0, EXA_EXCERPT_CHARS);
    } else {
      text = str(r.text).slice(0, TINYFISH_MAX_CHARS);
    }
    out.push({
      title: str(r.title).trim(),
      url,
      text,
      fetched: r.fetched === true,
    });
  }
  return out;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

/**
 * One result as the model sees it. Labels snippet vs fetched page so a
 * scrambled ticker dump is not treated as equal to an official table.
 */
export function formatGroundingResult(
  n: number,
  r: NormalizedResult,
  provider: SearchProviderId,
): string {
  const host = hostOf(r.url);
  const hostBit = host === "" ? "" : ` · ${host}`;
  const source =
    provider === "exa"
      ? `search excerpt${hostBit}`
      : r.fetched
        ? `fetched page${hostBit}`
        : `search snippet${hostBit}`;
  const url = neutralizeSearchMarkers(r.url);
  return `[${n}] **Title:** ${neutralizeSearchMarkers(r.title)}\n**URL:** [${url}](${url})\n**Source:** ${source}\n**Content:**\n${neutralizeSearchMarkers(r.text)}\n`;
}

/** Citations for the replay payload — see chatModel.buildSearchContext. */
export function sourcesFrom(results: NormalizedResult[]): SearchSource[] {
  return results.map((r, i) =>
    r.title === "" ? { n: i + 1, url: r.url } : { n: i + 1, title: r.title, url: r.url },
  );
}
